import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { Sidebar } from './layout/Sidebar';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { InboxPage } from './pages/Inbox';
import { TicketsPage } from './pages/Tickets';
import { CustomersPage } from './pages/Customers';
import { CompaniesPage } from './pages/Companies';
import { TemplatesPage } from './pages/Templates';
import { AutomationRulesPage } from './pages/AutomationRules';
import { SettingsPage } from './pages/Settings';
import { SgpAuthorizationsPage } from './pages/SgpAuthorizations';
import { SgpDashboardPage } from './pages/SgpDashboard';
import { UsersPage } from './pages/Users';
import { DepartmentsPage } from './pages/Departments';
import { AiIntelligencePage } from './pages/AiIntelligence';
import { AgentsPage } from './pages/Agents';
import { SdrMonitoringPage } from './pages/SdrMonitoring';

type ViewId = 'dashboard' | 'sdr-monitoring' | 'inbox' | 'tickets' | 'customers' | 'companies' | 'templates' | 'rules' | 'settings' | 'sgp-auth' | 'sgp' | 'users' | 'departments' | 'ai-intelligence' | 'agents';

const viewComponents: Record<ViewId, React.FC> = {
  dashboard: DashboardPage,
  'sdr-monitoring': SdrMonitoringPage,
  inbox: InboxPage,
  tickets: TicketsPage,
  customers: CustomersPage,
  companies: CompaniesPage,
  templates: TemplatesPage,
  rules: AutomationRulesPage,
  settings: SettingsPage,
  'sgp-auth': SgpAuthorizationsPage,
  sgp: SgpDashboardPage,
  users: UsersPage,
  departments: DepartmentsPage,
  'ai-intelligence': AiIntelligencePage,
  agents: AgentsPage,
};

const viewRoutes: Record<ViewId, string> = {
  dashboard: '/dashboard',
  'sdr-monitoring': '/sdr-monitoring',
  inbox: '/inbox',
  tickets: '/tickets',
  customers: '/customers',
  companies: '/companies',
  templates: '/templates',
  rules: '/rules',
  settings: '/settings',
  'sgp-auth': '/sgp-auth',
  sgp: '/sgp',
  users: '/users',
  departments: '/departments',
  'ai-intelligence': '/ai-intelligence',
  agents: '/agents',
};

const viewByRoute = Object.fromEntries(
  Object.entries(viewRoutes).map(([view, route]) => [route, view as ViewId]),
) as Record<string, ViewId>;

export default function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;
  const activeView = viewByRoute[pathname];

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <span>Carregando...</span>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  if (!activeView) {
    return <Navigate to={viewRoutes.dashboard} replace />;
  }

  const ActiveComponent = viewComponents[activeView];

  return (
    <div className="shell">
      <Sidebar
        activeView={activeView}
        onNavigate={(view) => navigate(viewRoutes[view as ViewId] ?? viewRoutes.dashboard)}
      />
      <main className="workspace">
        <ActiveComponent />
      </main>
    </div>
  );
}
